import ScriptedTransportBase from "./scriptedTransportBase";
import onboardingCopilotStore from "Stores/onboardingCopilotStore";
import buildOnboardingScenario, {
  emitWelcome,
  emitResume,
  advanceFromState,
  stopRefineWatch,
  emitOffScriptBridge,
  beginDetour
} from "./onboardingScenario";
import buildDigestScenario, { emitDigest } from "./digestScenario";
import { fetchProspectPage } from "./onboardingServices";
import SseAgentTransport from "./SseAgentTransport";
import { AGENT_EVENTS, CLIENT_EVENTS, BLOCK } from "./agentContract";
import userDetail from "Stores/userDetail";

/**
 * Client-side scripted transport for the copilot onboarding flows. Reuses the
 * shared step-engine (scriptedTransportBase); each step's run() awaits real
 * service calls. The deterministic state machine, gating and resume live in
 * onboardingCopilotStore, which is passed as the engine ctx so steps read and
 * write the collected artifacts. Selected by createAgentTransport(mode).
 *
 * Hybrid: the scripted flow owns progression, but a free-text question the
 * current step does not expect is routed to the live MCP copilot (a lazily
 * created SseAgentTransport "consultant") for a read-only answer, after which
 * the current step is re-emitted so the flow stays on the rail.
 */
// What the scripted flow already knows, in the shape the orchestrator can use.
// Mirrors the Step 8 handoff brief, which stopped the build flow re-asking
// questions the user had already answered.
// Blocks that put a decision in front of the user. While one of these is on
// screen from the consultant, it owns the turn.
const AWAITING_BLOCKS = [
  BLOCK.QUICK_REPLIES,
  BLOCK.CTA_CHIPS,
  BLOCK.QUESTION_FORM,
  BLOCK.CONFIRMATION
];

const onboardingBrief = ctx => {
  const a = (ctx && ctx.artifacts) || {};
  const icp = a.icpFilters || {};
  const join = v => (Array.isArray(v) ? v.filter(Boolean).join(", ") : "");
  const out = {};
  if (a.savedProfileId) out.sellerProfileId = a.savedProfileId;
  if (a.draftProfile && a.draftProfile.company_name) out.company = a.draftProfile.company_name;
  const audience = [join(icp.title), join(icp.level), join(icp.industry), join(icp.country)]
    .filter(Boolean)
    .join(" | ");
  if (audience) out.icpSummary = audience;
  // The summary is prose; the agent needs the real filters to reproduce the
  // search. Without them a re-search after a reload ran unfiltered.
  const filters = {};
  ["title", "level", "department", "industry", "employeeCount", "country"].forEach(k => {
    if (Array.isArray(icp[k]) && icp[k].length) filters[k] = icp[k];
  });
  if (Object.keys(filters).length) out.icpFilters = filters;
  if (a.searchTotal) out.lastSearchTotal = a.searchTotal;
  // Hand over the rows the search actually loaded. Telling the agent a search
  // happened was not enough: it re-ran its own and came back with 0 where the
  // scripted one had found over a thousand.
  try {
    // eslint-disable-next-line global-require
    const rows = require("./onboardingServices").loadedProspects();
    if (rows && rows.length) out.loadedProspects = rows;
  } catch (e) {
    /* rows unavailable: the brief still carries the ICP and totals */
  }
  if (a.listId) out.listId = a.listId;
  if (a.listName) out.listName = a.listName;
  if (a.listSize) out.listSize = a.listSize;
  if (a.sequenceId) out.sequenceId = a.sequenceId;
  return out;
};

class OnboardingAgentTransport extends ScriptedTransportBase {
  constructor(mode = "onboarding") {
    const digest = mode === "digest";
    super({
      steps: digest ? buildDigestScenario() : buildOnboardingScenario(),
      ctx: onboardingCopilotStore,
      // Only the digest keeps the canned nudge; the new-user flow answers
      // off-script via the consultant (offScriptHandler set below).
      fallbackText:
        "Use the buttons above to keep going. You can also close this and explore on your own any time.",
      onConnected: async api => {
        if (digest) return emitDigest(api);
        onboardingCopilotStore.hydrate(userDetail?.userInfo?.memberId);
        // Coming back from the sequence hand-off: continue silently from the
        // state, not with a "Welcome back?" prompt the user never left for.
        if (onboardingCopilotStore._resumeDirect) {
          onboardingCopilotStore._resumeDirect = false;
          return advanceFromState(api, onboardingCopilotStore);
        }
        const resuming =
          onboardingCopilotStore.status === "active" && onboardingCopilotStore.step > 0;
        return resuming ? emitResume(api) : emitWelcome(api);
      }
    });
    this.mode = mode;
    this._consultant = null;
    this._consultantOff = null;
    this._pendingConsultant = null;

    // Both flows answer a free-text question with the live copilot. The digest
    // is one-shot (no steps), so it is answer-only; onboarding also re-emits
    // the current step afterwards to return the user to the rail.
    this._offScriptHandler = (api, evt, ctx) => this._handleOffScript(api, evt, ctx);

    // Tear the consultant down with the onboarding transport so its EventSource
    // does not outlive the flow.
    // send is an instance FIELD on the base, so wrap it here rather than with
    // super.send (which does not exist for class fields). While the consultant
    // holds the turn, its card's answer must go back to the consultant: the
    // scripted matcher drops any non-USER_MESSAGE event no step consumes, so
    // the pick went nowhere and the card sat on "Waiting on your pick".
    const baseSend = this.send;
    this.send = clientEvent => {
      if (
        this._consultantAwaitingUser &&
        this._consultant &&
        clientEvent &&
        clientEvent.type !== CLIENT_EVENTS.USER_MESSAGE
      ) {
        this._consultantAwaitingUser = false;
        this._consultant.send(clientEvent);
        return undefined;
      }
      return baseSend(clientEvent);
    };

    const baseConnect = this.connect;
    this.connect = (conversationId, onEvent) => {
      const off = baseConnect(conversationId, onEvent);
      return () => {
        off();
        stopRefineWatch();
        this._teardownConsultant();
      };
    };
  }

  // The Step 4 prospect TABLE_REF opens the shared TableSidePanel, which pages
  // through the transport. fetchProspectPage pages the live discover search so
  // the panel shows every matching record in view mode (see fetchProspectPage).
  fetchTablePage = (tableId, page, pageSize) => fetchProspectPage(tableId, page, pageSize);

  // Active off-script detour ({ api }) until the consultant hands back.
  _detour = null;

  interrupt = () => {
    this.interrupted = true;
    if (this._consultant && this._consultant.interrupt) this._consultant.interrupt();
  };

  _teardownConsultant = () => {
    this._detour = null;
    try {
      if (this._consultantOff) this._consultantOff();
    } catch (e) {
      /* best-effort */
    }
    this._consultant = null;
    this._consultantOff = null;
    this._pendingConsultant = null;
  };

  // Answer an off-script question with the live MCP copilot, then re-emit the
  // current step. Read-only: the consultant answers; it never advances the
  // flow or mutates data (see the onboarding context flag + the MCP guard).
  _handleOffScript = async (api, evt, ctx) => {
    const text = (evt.text || "").trim();
    if (!text) return;
    // The hand-back ("ask more, or continue?") is emitted by the consultant
    // event handler when a consultant turn ends WITHOUT leaving a question on
    // screen. Doing it here only covered the first turn: when the consultant
    // asked something (a list picker), the user's answer went straight to the
    // consultant and its follow-up turn ended with nobody handing the mic back,
    // so the flow stranded before enrich.
    this._detour = this.mode === "digest" ? null : { api };
    try {
      if (this.mode !== "digest") await beginDetour(ctx);
      await this._askConsultant(text, ctx);
    } catch (e) {
      /* consultant failed; the turn-end handler still hands back */
    }
  };

  // Called on every consultant turn end. Hands the mic back once the consultant
  // is no longer waiting on the user, then closes the detour.
  _maybeHandBack = () => {
    const detour = this._detour;
    if (!detour || this.disconnected || this._consultantAwaitingUser) return;
    this._detour = null;
    emitOffScriptBridge(detour.api).catch(() => {
      /* best-effort */
    });
  };

  _askConsultant = (text, ctx) =>
    new Promise(resolve => {
      if (this.disconnected) {
        resolve();
        return;
      }
      if (!this._consultant) {
        this._consultant = new SseAgentTransport();
        this._consultantOff = this._consultant.connect(null, evt => {
          // Forward the answer into the same thread the scripted flow uses.
          if (this.onEvent) this.onEvent(evt);
          if (evt.type === AGENT_EVENTS.TURN_START) this._consultantAwaitingUser = false;
          // An interactive block means the consultant has asked the user
          // something and is waiting. The script must not speak over it: doing
          // so ended the turn and left those options permanently disabled.
          if (
            evt.type === AGENT_EVENTS.BLOCK_START &&
            evt.block &&
            AWAITING_BLOCKS.indexOf(evt.block.type) >= 0
          ) {
            this._consultantAwaitingUser = true;
          }
          if (
            evt.type === AGENT_EVENTS.TURN_END ||
            evt.type === AGENT_EVENTS.AGENT_ERROR
          ) {
            // Nothing here: whether the consultant still owns the turn is
            // decided by what it RENDERED (see AWAITING_BLOCKS below), not by
            // evt.await -- the orchestrator leaves that as "user_input" for
            // every card except ask_user forms, so it cannot distinguish a
            // finished answer from a question still waiting for a pick.
            const done = this._pendingConsultant;
            this._pendingConsultant = null;
            if (done) done();
            this._maybeHandBack();
          }
        });
      }
      this._pendingConsultant = resolve;
      // Safety net: never hang the flow if the answer turn never ends.
      const guard = setTimeout(() => {
        if (this._pendingConsultant === resolve) {
          this._pendingConsultant = null;
          resolve();
        }
      }, 60000);
      const wrapped = () => {
        clearTimeout(guard);
        resolve();
      };
      this._pendingConsultant = wrapped;

      // Lazy require: copilotContext pulls in the contacts stores (and, in
      // jest, the quill transform chain), so keep it out of module load.
      let base = {};
      try {
        // eslint-disable-next-line global-require
        base = require("./copilotContext").getCopilotContext() || {};
      } catch (e) {
        base = {};
      }
      this._consultant.send({
        type: CLIENT_EVENTS.USER_MESSAGE,
        text,
        // Tells the orchestrator the user is mid-onboarding: answer briefly,
        // read-only, and do not take over the flow.
        context: {
          ...base,
          // Steps 1-7 run entirely client-side, so the orchestrator has no
          // record of the search, the ICP or the list unless we hand it over.
          // Without this it answered "I don't have a recent search to work
          // from" seconds after the script had found hundreds of people.
          onboarding: {
            active: true,
            step: ctx.step,
            awaiting: ctx.awaiting,
            ...onboardingBrief(ctx)
          }
        }
      });
    });
}

export default OnboardingAgentTransport;
